import './PausedMonitorsPopup.scss';
import React, { useState } from 'react';
import { Dialog } from 'primereact/dialog';
import { Checkbox } from 'primereact/checkbox';
import { Button } from 'primereact/button';
import SimpleBar from 'simplebar-react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { useAccount } from '../../../contexts/account/AccountContexts';
import {
  bulkRunMonitors,
  getPausedMonitors,
  PausedMonitorItem,
} from '../../../services/monitor/prompt/PromptService';
import { NOTIFICATIONS_QUERY_KEY } from '../../../api/query/notifications/keys';
import { NavLink } from 'react-router-dom';
import { AGENCY_HUB } from '../../../routes/path';
import { isAdminOrOwner } from '../../../utils/utils';
import { promptFitRejection } from '../../../helpers/prompt/promptFit';

export interface PausedMonitorsPopupProps {
  visible: boolean;
  onHide: () => void;
}

const PausedMonitorsPopup: React.FC<PausedMonitorsPopupProps> = ({ visible, onHide }) => {
  const { t } = useTranslation();
  const { accountResponse, refetchAccountSilently, refetchProjectsSilently } = useAccount();
  const queryClient = useQueryClient();
  const [selectedIds, setSelectedIds] = useState<number[]>([]);
  // The allocation rule's refusal (whole batch, nothing resumed), with the project named; the popup
  // is a dialog itself, so the message stays inline. OWNER / ADMIN get the link to the hub.
  const [fitError, setFitError] = useState<string | null>(null);
  const canOpenHub = Boolean(accountResponse && isAdminOrOwner(accountResponse.role));

  const accountId = accountResponse?.accountId;
  const maxMonitors = accountResponse?.maxMonitorCount ?? 0;
  const createdMonitors = accountResponse?.createdMonitorCount ?? 0;
  const availableSlots = maxMonitors - createdMonitors;

  const { data: pausedMonitors } = useQuery({
    queryKey: ['paused-monitors', accountId],
    queryFn: () => getPausedMonitors(accountId!),
    // Only fetch when the popup is open — avoids loading on initial mount
    enabled: visible && !!accountId,
    staleTime: 0,
  });

  const { mutate: runMonitors, isPending } = useMutation({
    mutationFn: (ids: number[]) => bulkRunMonitors(ids),
    onSuccess: () => {
      onHide();
      setSelectedIds([]);
      refetchAccountSilently();
      // Activating monitors changes each project's active/paused counts. Refresh BOTH list sources so
      // the projects table updates: `projectResponse` (V1 reads counts from context) and the
      // `projects-page-*` queries (V2 renders from those). Without this the list counts stay stale.
      refetchProjectsSilently();
      queryClient.invalidateQueries({ queryKey: ['projects-page-ungrouped'] });
      queryClient.invalidateQueries({ queryKey: ['projects-page-group'] });
      // Clear the notifications badge after activating — the backend will re-evaluate eligibility
      queryClient.invalidateQueries({ queryKey: [NOTIFICATIONS_QUERY_KEY] });
    },
    onError: (error) => {
      const rejection = promptFitRejection(error);
      if (!rejection) {
        return;
      }
      const name = (pausedMonitors ?? []).find((m) => m.projectId === rejection.projectId)?.projectLabel ?? '';
      setFitError(
        rejection.kind === 'ALLOCATION_EXCEEDED'
          ? t('allocationExceeded', { name, used: rejection.used, allocation: rejection.allocation })
          : t(canOpenHub ? 'projectPausedInHub' : 'projectPausedInHubAskAdmin', { name })
      );
    },
  });

  const handleToggle = (id: number) => {
    setFitError(null);
    setSelectedIds((prev) => {
      if (prev.includes(id)) {
        return prev.filter((i) => i !== id);
      }
      if (prev.length >= availableSlots) {
        return prev;
      }
      return [...prev, id];
    });
  };

  const handleActivate = () => {
    if (selectedIds.length > 0) {
      runMonitors(selectedIds);
    }
  };

  const handleHide = () => {
    setSelectedIds([]);
    setFitError(null);
    onHide();
  };

  const monitors = pausedMonitors ?? [];

  const groupedByProject = monitors.reduce(
    (acc, monitor) => {
      const key = monitor.projectId;
      if (!acc[key]) {
        acc[key] = { label: monitor.projectLabel, monitors: [] };
      }
      acc[key].monitors.push(monitor);
      return acc;
    },
    {} as Record<number, { label: string; monitors: PausedMonitorItem[] }>
  );

  return (
    <Dialog
      visible={visible}
      onHide={handleHide}
      header={t('pausedMonitorsPopupTitle')}
      className="dialog-box paused-monitors-popup"
      closable
      draggable={false}
    >
      <div className="paused-monitors-popup__content">
        <p className="paused-monitors-popup__desc">{t('pausedMonitorsPopupDesc')}</p>
        <p className="paused-monitors-popup__slots">
          {t('pausedMonitorsPopupSlots', { available: availableSlots, total: maxMonitors })}
        </p>
        {fitError && (
          <p className="paused-monitors-popup__error">
            {fitError}
            {canOpenHub && (
              <NavLink to={AGENCY_HUB} onClick={handleHide}>
                {t('agencyHub')}
              </NavLink>
            )}
          </p>
        )}

        <SimpleBar style={{ maxHeight: '320px' }} autoHide={false}>
          <div className="paused-monitors-popup__list">
            {Object.entries(groupedByProject).map(([projectId, group]) => (
              <div key={projectId} className="paused-monitors-popup__project">
                <div className="paused-monitors-popup__project-label">{group.label}</div>
                {group.monitors.map((monitor) => (
                  <label key={monitor.id} className="paused-monitors-popup__item">
                    <Checkbox
                      checked={selectedIds.includes(monitor.id)}
                      onChange={() => handleToggle(monitor.id)}
                      disabled={
                        !selectedIds.includes(monitor.id) && selectedIds.length >= availableSlots
                      }
                    />
                    <span className="paused-monitors-popup__prompt">{monitor.prompt}</span>
                  </label>
                ))}
              </div>
            ))}
          </div>
        </SimpleBar>

        <div className="paused-monitors-popup__actions">
          <Button
            className="btn btn--primary btn--sm"
            icon={<span className="ico--run ico--xs" />}
            label={t('pausedMonitorsPopupActivate')}
            onClick={handleActivate}
            disabled={selectedIds.length === 0 || isPending}
            loading={isPending}
          />
        </div>
      </div>
    </Dialog>
  );
};

export default PausedMonitorsPopup;
